import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiEntityStatistics } from '../../../../api/hexa/ApiEntityStatistics';
import { TagsHistogramInterval } from '../../../../api/hexa/models/EntityStatisticsModels';
import { sgrColors } from '../../../../ui/sgr/SgrColors';
import FilterFieldBox from '../FilterFieldBox';
import {
  ChartFrame,
  DateRangeFilter,
  STANDARD_FIELD_PROPS,
} from '../StatisticsFilters';
import StatisticsRankedList from '../StatisticsRankedList';
import StatisticsSection from '../StatisticsSection';
import TagsHistogramChart, {
  CATEGORICAL_COLORS,
  MAX_PLOTTED_TAGS,
} from '../TagsHistogramChart';
import TooltipLink, { API_DOCS_URLS } from '../TooltipLink';
import { useMostSearchedStats } from '../useMostSearchedStats';
import { useTagsHistogramStats } from '../useTagsHistogramStats';

interface TagsTabProps {
  onFetchError: (message: string) => void;
}

const TagsTab = ({ onFetchError }: TagsTabProps) => {
  const { t } = useTranslation();

  const fetchTags = useCallback(
    (params: Parameters<typeof ApiEntityStatistics.mostSearchedTags>[0]) =>
      ApiEntityStatistics.mostSearchedTags(params).then(
        (response) => response.most_searched
      ),
    []
  );
  const fetchTagsHistogram = useCallback(
    (params: Parameters<typeof ApiEntityStatistics.tagsHistogram>[0]) =>
      ApiEntityStatistics.tagsHistogram(params),
    []
  );

  const tagsStats = useMostSearchedStats(fetchTags, onFetchError);
  const tagsHistogramStats = useTagsHistogramStats(
    fetchTagsHistogram,
    onFetchError
  );

  // Every tag in the histogram can be plotted, ordered by total so the picker lists the busiest first
  const histogramTags = useMemo(
    () =>
      [...tagsHistogramStats.histogram]
        .sort((a, b) => b.total - a.total)
        .map((entry) => entry.tag),
    [tagsHistogramStats.histogram]
  );
  const histogramTotals = useMemo(
    () =>
      new Map(
        tagsHistogramStats.histogram.map((entry) => [entry.tag, entry.total])
      ),
    [tagsHistogramStats.histogram]
  );

  // A date change can drop selected tags from the data; with none left, the top tags are plotted instead
  const plottedTags = useMemo(() => {
    const available = tagsHistogramStats.selectedTags.filter((tag) =>
      histogramTotals.has(tag)
    );
    return available.length > 0
      ? available
      : histogramTags.slice(0, MAX_PLOTTED_TAGS);
  }, [tagsHistogramStats.selectedTags, histogramTags, histogramTotals]);
  const isPlottedTagsLimitReached =
    tagsHistogramStats.selectedTags.length >= MAX_PLOTTED_TAGS;

  // Both sections count tags from the same searches, so they share the tooltip
  const subtitleTooltip = (
    <Trans
      i18nKey="entity_statistics.tags_subtitle_tooltip"
      components={{
        entitiesLink: <TooltipLink href={API_DOCS_URLS.entities} />,
        batchLink: <TooltipLink href={API_DOCS_URLS.entitiesBatch} />,
      }}
    />
  );

  return (
    <>
      <StatisticsSection
        title={t('entity_statistics.tags_title')}
        subtitle={t('entity_statistics.tags_subtitle')}
        subtitleTooltip={subtitleTooltip}
        icon={<LeaderboardOutlinedIcon />}
        filtersTitle={t('entity_statistics.filters')}
        onResetFilters={tagsStats.resetFilters}
        isLoading={tagsStats.isLoading}
        loadingText={t('entity_statistics.tags_loading')}
        isEmpty={tagsStats.results.length === 0}
        emptyText={t('entity_statistics.tags_empty')}
        filters={<DateRangeFilter state={tagsStats} />}
      >
        <StatisticsRankedList
          valueLabel={t('entity_statistics.search_count')}
          entries={tagsStats.results.map((entry, index) => ({
            id: `${entry.tag}-${index}`,
            label: entry.tag,
            value: entry.searched_count,
          }))}
        />
      </StatisticsSection>

      <StatisticsSection
        variant="chart"
        title={t('entity_statistics.tags_histogram_title')}
        subtitle={t('entity_statistics.tags_histogram_subtitle')}
        subtitleTooltip={subtitleTooltip}
        icon={<ShowChartOutlinedIcon />}
        filtersTitle={t('entity_statistics.filters')}
        onResetFilters={tagsHistogramStats.resetFilters}
        isLoading={tagsHistogramStats.isLoading}
        loadingText={t('entity_statistics.tags_histogram_loading')}
        isEmpty={tagsHistogramStats.histogram.length === 0}
        emptyText={t('entity_statistics.tags_histogram_empty')}
        filters={
          <>
            <DateRangeFilter state={tagsHistogramStats} />
            <FilterFieldBox
              label={t('entity_statistics.interval_label')}
              icon={<TimelineOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
            >
              <Select
                {...STANDARD_FIELD_PROPS}
                value={tagsHistogramStats.intervalUnit}
                onChange={(event) =>
                  tagsHistogramStats.setIntervalUnit(
                    event.target.value as TagsHistogramInterval
                  )
                }
              >
                <MenuItem value="day">
                  {t('entity_statistics.interval_day')}
                </MenuItem>
                <MenuItem value="month">
                  {t('entity_statistics.interval_month')}
                </MenuItem>
                <MenuItem value="year">
                  {t('entity_statistics.interval_year')}
                </MenuItem>
              </Select>
            </FilterFieldBox>
            <FilterFieldBox
              label={t('entity_statistics.tags_histogram_tags_label', {
                max: MAX_PLOTTED_TAGS,
              })}
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: '0.875rem' }} />}
              action={
                tagsHistogramStats.selectedTags.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => tagsHistogramStats.setSelectedTags([])}
                    sx={{
                      color: sgrColors.backdrop,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      minWidth: 0,
                      padding: '0 0.25rem',
                      lineHeight: 1.5,
                      '&:hover': { color: sgrColors.navy },
                    }}
                  >
                    {t('entity_statistics.tags_histogram_tags_clear')}
                  </Button>
                )
              }
            >
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={histogramTags}
                value={tagsHistogramStats.selectedTags}
                onChange={(_, value) => tagsHistogramStats.setSelectedTags(value)}
                // Each plotted tag needs its own color, so the selection stops at the palette size
                getOptionDisabled={(tag) =>
                  isPlottedTagsLimitReached &&
                  !tagsHistogramStats.selectedTags.includes(tag)
                }
                renderOption={(props, tag, { selected }) => (
                  <li {...props} key={tag}>
                    <Checkbox checked={selected} size="small" />
                    <ListItemText
                      primary={tag}
                      secondary={histogramTotals.get(tag)?.toLocaleString()}
                    />
                  </li>
                )}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag}
                      size="small"
                      label={tag}
                      // Matches the tag's line in the chart, so the chip doubles as its legend entry
                      icon={
                        <Box
                          component="span"
                          sx={{
                            width: '0.5rem',
                            height: '0.5rem',
                            borderRadius: '50%',
                            flexShrink: 0,
                            backgroundColor:
                              CATEGORICAL_COLORS[plottedTags.indexOf(tag)] ??
                              sgrColors.grey_cloud,
                          }}
                        />
                      }
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder={
                      tagsHistogramStats.selectedTags.length === 0
                        ? t('entity_statistics.tags_histogram_tags_placeholder', {
                            max: MAX_PLOTTED_TAGS,
                          })
                        : undefined
                    }
                  />
                )}
              />
            </FilterFieldBox>
          </>
        }
      >
        <ChartFrame>
          <TagsHistogramChart
            histogram={tagsHistogramStats.histogram}
            plottedTags={plottedTags}
            interval={tagsHistogramStats.intervalUnit}
          />
        </ChartFrame>
      </StatisticsSection>
    </>
  );
};

export default TagsTab;
